import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import MaskedVideoPlayer from "@/components/MaskedVideoPlayer";
import { UploadCloud, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Auditions() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    project_id: "", title: "", description: "",
    talent_name: "", role_applying_for: "main_talent",
    duration_sec: 120, file_size_bytes: 4500000,
  });

  const reload = async () => {
    const { data } = await api.get("/auditions");
    setList(data);
  };
  useEffect(() => {
    reload();
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/auditions/upload", form);
      toast.success("Audition uploaded (mocked YouTube vault · unlisted)");
      setForm({ ...form, title: "", description: "", talent_name: "" });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="auditions-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Disguised Video Engine</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Auditions Vault</h1>
          <p className="mt-3 text-sm text-white/55 max-w-xl flex items-center gap-2">
            <EyeOff size={14} className="text-[#F5B82E]" />
            Headless upload routing. Privacy strictly enforced as <span className="uppercase tracking-wider text-white/80">unlisted</span>.
          </p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="btn-themed h-11 px-6 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="new-audition-btn">
              <UploadCloud size={14} className="mr-2" /> Upload Audition
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight">New Audition</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <Field label="Project (optional)">
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-black/40 border-white/15"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Title">
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="bg-black/40 border-white/15" data-testid="aud-title" />
              </Field>
              <Field label="Talent Name">
                <Input value={form.talent_name} onChange={(e) => setForm({ ...form, talent_name: e.target.value })}
                  className="bg-black/40 border-white/15" data-testid="aud-talent" />
              </Field>
              <Field label="Role Applying For">
                <Input value={form.role_applying_for} onChange={(e) => setForm({ ...form, role_applying_for: e.target.value })}
                  className="bg-black/40 border-white/15" data-testid="aud-role" />
              </Field>
              <Field label="Description">
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-black/40 border-white/15" data-testid="aud-desc" />
              </Field>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5B82E]">
                MOCKED: file routes to corporate YouTube vault (unlisted). Real OAuth credentials wire later.
              </div>
              <Button onClick={submit} className="btn-themed w-full h-11 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="aud-submit">
                Upload (mock) →
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Player + list */}
      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <div>
          {selected ? (
            <>
              <MaskedVideoPlayer
                src={null}
                ytId={selected.youtube_video_id}
                title={selected.title}
              />
              <div className="mt-4 cs-card rounded-xl p-5">
                <div className="font-display text-xl uppercase tracking-tight">{selected.title}</div>
                <div className="mt-1 text-sm text-white/60">
                  {selected.talent_name} · applying for {selected.role_applying_for}
                </div>
                <div className="mt-3 text-xs text-white/45">{selected.description}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Source ID · {selected.youtube_video_id} · {selected.youtube_privacy}
                </div>
              </div>
            </>
          ) : (
            <div className="cs-card rounded-xl aspect-video flex items-center justify-center text-white/30 text-sm uppercase tracking-[0.3em]">
              Select an audition →
            </div>
          )}
        </div>
        <div className="space-y-3" data-testid="aud-list">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1">Vault</div>
          {list.length === 0 && <div className="text-white/40 text-sm">No auditions yet.</div>}
          {list.map((a) => (
            <button key={a.id} onClick={() => setSelected(a)}
              className={`w-full text-left cs-card rounded-xl p-4 hover:border-white/30 transition ${selected?.id === a.id ? "ring-1" : ""}`}
              style={selected?.id === a.id ? { boxShadow: "0 0 0 1px var(--association-theme)" } : {}}
              data-testid={`aud-item-${a.id}`}>
              <div className="text-sm font-medium truncate">{a.title || "Untitled audition"}</div>
              <div className="text-xs text-white/55 mt-0.5">{a.talent_name}</div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/40">{a.youtube_privacy}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
